import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaImage, FaTimes } from 'react-icons/fa';
import { getTicketImages, uploadTicketImage } from '../../services/ticketService';
import { useApp } from '../../context/AppContext';

const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];

const isImageFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  const name = String(file.name || '').toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  h3,
  h4 {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  input {
    display: none;
  }
`;

const ImagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ImageItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const ImagePreview = styled.img`
  width: 100%;
  max-width: 520px;
  max-height: 320px;
  border-radius: 10px;
  object-fit: cover;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-primary);
  cursor: zoom-in;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  &:focus-visible {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
  }
`;

const ImageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 520px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ImageName = styled.span`
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
  cursor: zoom-out;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.25rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #111;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #404040;
    color: white;
    transform: rotate(90deg);
  }
`;

const LightboxImage = styled.img`
  max-width: min(920px, 100%);
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  cursor: default;
`;

const TicketImagesSection = ({
  ticketId,
  refreshKey = 0,
  allowUpload = true,
  title = 'Imagens',
  compactTitle = false,
  onCountChange,
}) => {
  const { addToast } = useApp();
  const inputRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const closeLightbox = useCallback(() => setLightboxUrl(null), []);

  const fetchImages = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const response = await getTicketImages(ticketId);
      const list = Array.isArray(response) ? response : [];
      setImages(list);
      onCountChangeRef.current?.(list.length);
    } catch (err) {
      console.error('Erro ao carregar imagens do ticket:', err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages, refreshKey]);

  useEffect(() => {
    if (!lightboxUrl) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxUrl, closeLightbox]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!ticketId || files.length === 0) return;

    const imagesToUpload = files.filter(isImageFile);
    if (imagesToUpload.length === 0) {
      addToast?.('Selecione um arquivo de imagem.', 'error');
      return;
    }

    const oversized = imagesToUpload.filter((file) => file.size > MAX_IMAGE_BYTES);
    if (oversized.length > 0) {
      addToast?.(
        'Cada imagem deve ter no máximo 50 MB. Remova os arquivos maiores e tente de novo.',
        'error'
      );
    }

    const accepted = imagesToUpload.filter((file) => file.size <= MAX_IMAGE_BYTES);
    if (accepted.length === 0) return;

    try {
      setUploading(true);
      await Promise.all(accepted.map((file) => uploadTicketImage(ticketId, file)));
      await fetchImages();
      addToast?.(
        accepted.length === 1 ? 'Imagem enviada.' : `${accepted.length} imagens enviadas.`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao enviar imagem:', err);
      addToast?.(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          (err?.response?.status
            ? `Falha ao enviar imagem (${err.response.status})`
            : 'Falha ao enviar a imagem.'),
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  const openLightbox = (url) => {
    if (!url) return;
    setLightboxUrl(url);
  };

  const handlePreviewKeyDown = (event, url) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLightbox(url);
    }
  };

  if (!ticketId) return null;

  const TitleTag = compactTitle ? 'h4' : 'h3';

  return (
    <Section aria-label="Imagens do ticket">
      <SectionHeader>
        <TitleTag>
          <FaImage aria-hidden />
          {title}
          {images.length > 0 ? ` (${images.length})` : ''}
        </TitleTag>
        {allowUpload && (
          <UploadButton $disabled={uploading}>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.bmp"
              multiple
              disabled={uploading}
              onChange={handleFilesSelected}
            />
            {uploading ? 'Enviando...' : 'Anexar imagem'}
          </UploadButton>
        )}
      </SectionHeader>

      {loading && images.length === 0 ? (
        <EmptyHint>Carregando imagens...</EmptyHint>
      ) : images.length === 0 ? (
        <EmptyHint>Nenhuma imagem anexada a este ticket.</EmptyHint>
      ) : (
        <ImagesList>
          {images.map((image, index) => (
            <ImageItem key={image.url || image.name || index}>
              <ImagePreview
                src={image.url}
                alt={image.name || 'Imagem'}
                role="button"
                tabIndex={0}
                aria-label={`Ampliar ${image.name || 'imagem'}`}
                onClick={() => openLightbox(image.url)}
                onKeyDown={(event) => handlePreviewKeyDown(event, image.url)}
              />
              <ImageMeta>
                <ImageName>{image.name || 'Imagem'}</ImageName>
              </ImageMeta>
            </ImageItem>
          ))}
        </ImagesList>
      )}

      {lightboxUrl && (
        <Lightbox onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Visualização ampliada">
          <LightboxClose type="button" onClick={closeLightbox} aria-label="Fechar">
            <FaTimes />
          </LightboxClose>
          <LightboxImage
            src={lightboxUrl}
            alt="Visualização ampliada"
            onClick={(event) => event.stopPropagation()}
          />
        </Lightbox>
      )}
    </Section>
  );
};

TicketImagesSection.propTypes = {
  ticketId: PropTypes.string,
  refreshKey: PropTypes.number,
  allowUpload: PropTypes.bool,
  title: PropTypes.string,
  compactTitle: PropTypes.bool,
  onCountChange: PropTypes.func,
};

export default TicketImagesSection;
